import axios, {
    AxiosError,
    type InternalAxiosRequestConfig,
    type AxiosResponse,
} from "axios";

// =========================
// TypeScript Interfaces
// =========================

// Interface untuk item di dalam antrian request yang gagal
interface FailedRequest {
    resolve: (token: string) => void;
    reject: (error: unknown) => void;
}

// Interface untuk memperluas config Axios dengan flag _retry
interface CustomAxiosRequestConfig extends InternalAxiosRequestConfig {
    _retry?: boolean;
}

// =========================
// Variables & State
// =========================
let isLoggingOut = false;
let isRefreshing = false;
let failedQueue: FailedRequest[] = []; // ✅ Sudah ditiping, bukan any[] lagi

// Variable Access Token (Disimpan di Memori)
let accessToken: string | null = null;

let onGlobalLogout: (() => void) | null = null;
let activeRequests = 0;
let onLoadingCallback: ((loading: boolean) => void) | null = null;

const API_URL =
    import.meta.env.VITE_APP_BASE_URL || "https://api-konaspi.undiksha.ac.id/api/v1";

// =========================
// Helper Functions
// =========================

export function setAccessToken(token: string | null) {
    accessToken = token;
}

export function setLoggingOut(state: boolean) {
    isLoggingOut = state;
}

export function registerGlobalLogoutHandler(callback: () => void) {
    onGlobalLogout = callback;
}

export function registerLoadingCallback(cb: (loading: boolean) => void) {
    onLoadingCallback = cb;
}

/**
 * Memproses antrian request yang tertunda.
 * @param error Error jika refresh gagal total
 * @param token Token baru jika refresh sukses
 */
const processQueue = (error: unknown, token: string | null = null) => {
    failedQueue.forEach((prom) => {
        if (error) {
            prom.reject(error);
        } else if (token) {
            prom.resolve(token);
        }
    });
    failedQueue = [];
};

// =========================
// Create Axios Instance
// =========================
export const axiosClient = axios.create({
    baseURL: API_URL,
    withCredentials: true, // ✅ WAJIB TRUE
    headers: {
        "Content-Type": "application/json",
        Accept: "application/json",
    },
});

// Instance khusus refresh (tanpa interceptor axiosClient)
const axiosRefresh = axios.create({
    baseURL: API_URL,
    withCredentials: true,
    headers: {
        Accept: "application/json",
    },
});

// =========================
// Request Interceptor
// =========================
axiosClient.interceptors.request.use(
    (config: InternalAxiosRequestConfig) => {
        // 1. Pasang Token dari Variable Memori
        if (accessToken && config.headers) {
            config.headers.Authorization = `Bearer ${accessToken}`;
        }

        // 2. Handle Loading Bar
        activeRequests++;
        if (onLoadingCallback) onLoadingCallback(true);

        return config;
    },
    (error: AxiosError) => {
        return Promise.reject(error);
    }
);

// =========================
// Response Interceptor
// =========================
const skipRefreshUrls = ["/login", "/logout", "/refresh"];

axiosClient.interceptors.response.use(
    (response: AxiosResponse) => {
        activeRequests--;
        if (activeRequests <= 0 && onLoadingCallback) onLoadingCallback(false);
        return response;
    },
    async (error: AxiosError) => {
        activeRequests--;
        if (activeRequests <= 0 && onLoadingCallback) onLoadingCallback(false);

        // Casting config ke Custom Interface agar properti _retry dikenali
        const originalRequest = error.config as CustomAxiosRequestConfig;

        if (!originalRequest) return Promise.reject(error);

        // Cek URL yang tidak boleh di-refresh
        if (
            isLoggingOut ||
            (originalRequest.url &&
                skipRefreshUrls.some((url) => originalRequest.url!.includes(url)))
        ) {
            return Promise.reject(error);
        }

        // ============================================================
        // LOGIKA REFRESH TOKEN (AUTO SILENT REFRESH)
        // ============================================================
        if (error.response?.status === 401 && !originalRequest._retry) {
            // Skenario: Token mati, tapi sedang ada proses refresh berjalan
            if (isRefreshing) {
                return new Promise<AxiosResponse>((resolve, reject) => {
                    failedQueue.push({
                        resolve: (token: string) => {
                            if (originalRequest.headers) {
                                originalRequest.headers.Authorization = `Bearer ${token}`;
                            }
                            resolve(axiosClient(originalRequest));
                        },
                        reject: (err: unknown) => {
                            reject(err);
                        },
                    });
                });
            }

            originalRequest._retry = true;
            isRefreshing = true;

            try {
                // Panggil endpoint refresh
                // Kita beri tipe return { access_token: string }
                const { data } = await axiosRefresh.post<{ access_token: string }>(
                    "/refresh"
                );
                const newAccessToken = data.access_token;

                // 1. Simpan token baru ke Memori
                setAccessToken(newAccessToken);

                // 2. Update header request yang gagal tadi
                axiosClient.defaults.headers.common[
                    "Authorization"
                ] = `Bearer ${newAccessToken}`;
                if (originalRequest.headers) {
                    originalRequest.headers.Authorization = `Bearer ${newAccessToken}`;
                }

                // 3. Proses request lain yang mengantri
                processQueue(null, newAccessToken);

                // 4. Ulangi request awal
                return axiosClient(originalRequest);
            } catch (err) {
                // Jika Refresh Token juga expired
                // Gunakan casting unknown atau AxiosError agar aman
                const e = err as AxiosError;
                console.error("Session expired / Refresh gagal:", e.message);

                processQueue(err, null);

                setAccessToken(null);
                delete axiosClient.defaults.headers.common["Authorization"];

                if (onGlobalLogout) onGlobalLogout();

                return Promise.reject(error);
            } finally {
                isRefreshing = false;
            }
        }

        return Promise.reject(error);
    }
);